import * as ko from "knockout";
import type {TBaseOrObservableType} from "../../../index";
import {lm} from "../../../appContext";
import {getDate, validateServerDate} from "../../../util/datetime";
import parsedHtml from "./parsed.html?raw";

export let template: string = parsedHtml;

export interface IIdpCertificateView {
    thumbprint: string
    notafter: string
    isprimary: boolean
}

export interface IParsedParams {
    readOnly: boolean
    entityId: TBaseOrObservableType<string>
    ssoUrl: TBaseOrObservableType<string>
    certificates: TBaseOrObservableType<IIdpCertificateView[]>
    sourceUrl: TBaseOrObservableType<string>
    sourceFileName: TBaseOrObservableType<string>
    refreshLast: TBaseOrObservableType<string>
    refreshNext: TBaseOrObservableType<string>
    refreshFailing: TBaseOrObservableType<boolean>
    onReplace?: () => void
}

export function metadataSourceLabel(url?: string | null, fileName?: string | null): string {
    const trimmedUrl = url?.trim() ?? "";
    if (trimmedUrl) {
        return lm.t({k: "sso.companyidp.parsed.fromurl", r: {url: trimmedUrl}}, undefined, true) ?? `From URL · ${trimmedUrl}`;
    }
    const name = fileName?.trim() ?? "";
    if (name) {
        return lm.t({k: "sso.companyidp.parsed.fromfile", r: {name}}, undefined, true) ?? `From file · ${name}`;
    }
    return lm.t("sso.companyidp.parsed.fromstored", undefined, true) ?? "From file";
}

export class viewModel {
    readonly readOnly: boolean;
    readonly entityId: TBaseOrObservableType<string>;
    readonly ssoUrl: TBaseOrObservableType<string>;
    readonly certificates: TBaseOrObservableType<IIdpCertificateView[]>;
    readonly refreshLast: TBaseOrObservableType<string>;
    readonly refreshNext: TBaseOrObservableType<string>;
    readonly refreshFailing: TBaseOrObservableType<boolean>;
    readonly sourceLabel: KnockoutComputed<string>;
    readonly showRefresh: KnockoutComputed<boolean>;
    readonly hasCertificates: KnockoutComputed<boolean>;
    readonly showReplace: boolean;
    readonly onReplace: () => void;

    constructor(params: IParsedParams) {
        this.readOnly = params.readOnly;
        this.entityId = params.entityId;
        this.ssoUrl = params.ssoUrl;
        this.certificates = params.certificates;
        this.refreshLast = params.refreshLast;
        this.refreshNext = params.refreshNext;
        this.refreshFailing = params.refreshFailing;
        this.sourceLabel = ko.pureComputed(() => metadataSourceLabel(ko.unwrap(params.sourceUrl), ko.unwrap(params.sourceFileName)));
        this.showRefresh = ko.pureComputed(() => !!ko.unwrap(this.refreshLast) || !!ko.unwrap(this.refreshNext) || ko.unwrap(this.refreshFailing));
        this.hasCertificates = ko.pureComputed(() => ko.unwrap(this.certificates).length > 0);
        this.showReplace = !params.readOnly;
        this.onReplace = params.onReplace ?? (() => undefined);
    }

    certDetail = (): string => lm.t("sso.companyidp.security.cert.detail") ?? "Signing";

    certExpires = (cert: IIdpCertificateView): string => {
        const formatted = serverDate(cert.notafter);
        const key = cert.isprimary ? "sso.companyidp.security.cert.active" : "sso.companyidp.security.cert.expires";
        const fallback = cert.isprimary ? `Active · Expires ${formatted}` : `Expires ${formatted}`;
        return lm.t({k: key, r: {date: formatted}}, undefined, true) ?? fallback;
    };
}

function serverDate(value: string | null | undefined): string {
    if (!value?.trim()) {
        return "";
    }
    return validateServerDate(value) ? getDate(value, true) : value;
}
